import { Link } from "react-router-dom";
import { ArrowRight, Users, BookOpen, Mic2, Sparkles, Mail, FileText, Download } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const FORMATS = [
  {
    icon: Users,
    title: "Workshops for care teams",
    body: "Practical, unhurried sessions on keeping the patient's voice at the center of care — for hospice and hospital staff, volunteers, and students.",
  },
  {
    icon: BookOpen,
    title: "Author conversations & signings",
    body: "An evening with the book: short readings, the stories behind it, and honest questions answered without hurry.",
  },
  {
    icon: Mic2,
    title: "Interviews & podcasts",
    body: "Thoughtful conversation about serious illness, caregiving, hospice, and grief — with room for the hard questions.",
  },
  {
    icon: Sparkles,
    title: "Something shaped for you",
    body: "Retreats, chaplaincy trainings, community nights — tell us who is gathering and what you need, and the visit takes shape from there.",
  },
];

const AUDIENCES = [
  "Hospices",
  "Hospitals & palliative care teams",
  "Caregiver groups",
  "Senior living communities",
  "Faith communities",
  "Conferences & organizations",
];

export default function Speaking() {
  return (
    <div className="bg-ivory">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Speaking & book signings
              </p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
                A visit shaped around your people.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-stone2">
                Derrick speaks with the same presence he brings to the
                bedside — unhurried, honest, and warm. Whether it is a room of
                clinicians or a circle of caregivers, the goal is the same:
                people leave with language for the things they have been
                carrying.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <div className="grid sm:grid-cols-2 gap-6">
          {FORMATS.map((f, i) => (
            <Reveal key={f.title} delay={0.07 * i} className="h-full">
              <div className="h-full bg-white border border-line rounded-md p-8 hover:border-gold/60 hover:shadow-[0_20px_40px_-28px_rgba(11,21,34,0.3)] transition-all duration-300">
                <f.icon className="w-5 h-5 text-gold" />
                <h2 className="mt-5 font-serif text-xl sm:text-2xl text-navy">
                  {f.title}
                </h2>
                <p className="mt-3 text-stone2 leading-relaxed text-[15px]">
                  {f.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div
            className="bg-sand border border-line rounded-md p-8 sm:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8"
            data-testid="speaking-handout-section"
          >
            <div className="flex items-start gap-5">
              <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gold-light">
                <FileText className="w-5 h-5 text-gold-hover" />
              </span>
              <div>
                <h2 className="font-serif text-2xl text-navy">
                  For book-signing guests
                </h2>
                <p className="mt-2 text-stone2 leading-relaxed max-w-2xl">
                  A one-page keepsake from signing events — a word of thanks
                  from Derrick, the book&rsquo;s core principles with a few
                  reflection prompts, and where to find the book and its
                  companions. Prints on standard letter paper.
                </p>
              </div>
            </div>
            <a
              href="/files/book-signing-guest-handout.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="speaking-handout-button"
              className="shrink-0 w-full lg:w-auto justify-center inline-flex items-center gap-2 bg-navy text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
            >
              Download the guest handout <Download className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </section>

      <section className="bg-sand border-y border-line">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
              Who invites him
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {AUDIENCES.map((a) => (
                <span
                  key={a}
                  className="bg-ivory border border-line rounded-full px-5 py-2.5 text-sm text-navy"
                >
                  {a}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative rounded-md overflow-hidden aspect-[4/5]">
                <img
                  src={IMAGES.derrick}
                  alt="Chaplain Derrick Hall in a tuxedo, photographed before the harbor"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  data-testid="speaking-photo"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl text-navy tracking-tight">
                What happens after you write
              </h2>
              <ul className="mt-8 space-y-5">
                {[
                  "Your note is read with care — every inquiry is.",
                  "Replies go to the email you shared, to talk through your event before anything is fixed.",
                  "Dates, format, and details are settled together, with your community in mind.",
                ].map((t, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="shrink-0 font-serif text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-stone2 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/booking"
                data-testid="speaking-cta-booking"
                className="mt-10 inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
              >
                <Mail className="w-4 h-4" /> Request a date
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
